// Tarefa atual (cartão verde do Figma): timer, pausar/retomar e "Finalizar" (P0.3).
import { useTranslation } from 'react-i18next';
import { pomodoroView } from '../domain/pomodoro';
import type { Task } from '../domain/task';
import { timerView } from '../domain/timer';
import { formatMinutes } from '../i18n/format';
import { useStore } from '../state/AppStore';
import { usePomodoro } from '../state/usePomodoro';
import { useNow } from '../state/useNow';
import { Icon } from './Icon';
import { TaskDescription } from './TaskDescription';
import { cls } from './NewTaskForm';
import { TimerText } from './TimerText';

export function CurrentTask({ task, canEdit, large = false }: { task: Task; canEdit: boolean; large?: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const now = useNow();
  const { state: pomo } = usePomodoro();
  const view = timerView(task, now);
  const running = task.status === 'running';
  const est = task.estimate_minutes;

  let subtitle: string;
  if (view.mode === 'countdown') subtitle = t('current.remaining', { left: formatMinutes(Math.ceil(view.seconds / 60), t), total: formatMinutes(est ?? 0, t) });
  else if (view.mode === 'overtime') subtitle = t('current.over', { over: formatMinutes(Math.ceil(view.seconds / 60), t), total: formatMinutes(est ?? 0, t) });
  else subtitle = t('current.noEstimate');

  const pomodoroOn = pomo.phase === 'focus' && pomo.taskId === task.id;

  return (
    <section
      className={cls('current', large && 'current--large', view.mode === 'overtime' && 'current--overtime', !running && 'current--paused')}
      aria-label={t('current.label')}
      data-testid="current-task"
    >
      <div className="current__eyebrow">
        <span>{running ? t('current.inFocus') : t('current.paused')}</span>
        {pomodoroOn && (
          <span className="badge badge--focus">
            <Icon name="timer" size={12} />
            {t('pomodoro.focusBadge', { time: pomodoroView(pomo, now).text })}
          </span>
        )}
      </div>
      <div className="current__top">
        <h2 className="current__title">{task.title}</h2>
        <TimerText view={view} className="current__timer" />
      </div>
      {task.description && <TaskDescription taskId={task.id} text={task.description} place="focus" />}
      <div className="current__bottom">
        <span className="current__sub">{subtitle}</span>
        <div className="current__buttons">
          {running ? (
            <button type="button" className="icon-btn icon-btn--outline" aria-label={t('current.pause')} disabled={!canEdit} onClick={() => void store.pause(task.id)}>
              <Icon name="pause" size={16} />
              {large && <span>{t('current.pause')}</span>}
            </button>
          ) : (
            <button type="button" className="icon-btn icon-btn--outline" aria-label={t('current.resume')} disabled={!canEdit} onClick={() => void store.start(task.id)}>
              <Icon name="play" size={14} filled />
              {large && <span>{t('current.resume')}</span>}
            </button>
          )}
          <button type="button" className="btn btn--primary" disabled={!canEdit} onClick={() => void store.finish(task.id)}>
            <Icon name="check" size={15} />
            {t('current.finish')}
          </button>
        </div>
      </div>
    </section>
  );
}
